export const COLORS = [
  "purple",
  "white",
  "blue",
  "yellow",
  "orange",
  "black",
  "red",
  "green",
] as const;
export type Color = (typeof COLORS)[number];
export type CardColor = Color | "locomotive";
export type Hand = Record<CardColor, number>;
export interface City {
  id: string;
  name: string;
  x: number;
  y: number;
}
export interface Route {
  id: string;
  from: string;
  to: string;
  length: number;
  color: Color | "gray";
  tunnel: boolean;
  locomotives: number;
  parallel?: string;
}
export interface Ticket {
  id: string;
  from: string;
  to: string;
  points: number;
  long: boolean;
}
export interface PlayerInput {
  id: string;
  name: string;
  picture?: string;
  color?: string;
}
export interface Player extends PlayerInput {
  color: string;
  hand: Hand;
  tickets: string[];
  trains: number;
  score: number;
  stationsRemaining: number;
}
export interface TicketOffer {
  ticketIds: string[];
  minKeep: number;
  initial: boolean;
}
export interface TunnelAttempt {
  playerId: string;
  routeId: string;
  color: CardColor;
  payment: CardColor[];
  revealed: CardColor[];
  extraCost: number;
}
export interface GameLog {
  turn: number;
  playerId: string | null;
  message: string;
}
export interface TicketResult extends Ticket {
  completed: boolean;
}
export interface PlayerResult {
  playerId: string;
  routePoints: number;
  ticketPoints: number;
  stationPoints: number;
  longestPath: number;
  longestBonus: number;
  total: number;
  completedTickets: number;
  tickets: TicketResult[];
  stationRoutes: Record<string, string | null>;
}
export interface GameState {
  version: 1;
  status: "setup" | "playing" | "finished";
  players: Player[];
  currentPlayerId: string;
  trainDeck: CardColor[];
  trainDiscard: CardColor[];
  market: CardColor[];
  ticketDeck: string[];
  ticketOffers: Record<string, TicketOffer>;
  claimedRoutes: Record<string, string>;
  stations: Record<string, string>;
  drawnTrainCards: number;
  tunnel: TunnelAttempt | null;
  turn: number;
  finalTurnsRemaining: number | null;
  finalRoundPlayerId: string | null;
  consecutivePasses: number;
  log: GameLog[];
  results: PlayerResult[] | null;
  winnerIds: string[];
}
export type GameAction =
  | { type: "chooseTickets"; ticketIds: string[] }
  | { type: "drawTrain"; source: "deck" | number }
  | {
      type: "claimRoute";
      routeId: string;
      color: CardColor;
      locomotives: number;
    }
  | { type: "resolveTunnel"; pay: boolean; locomotives?: number }
  | { type: "drawTickets" }
  | {
      type: "buildStation";
      cityId: string;
      color: CardColor;
      locomotives: number;
    }
  | { type: "pass" };
export interface PublicPlayer extends PlayerInput {
  color: string;
  trains: number;
  score: number;
  stationsRemaining: number;
  trainCardCount: number;
  ticketCount: number;
  ready: boolean;
}
export interface SelfView {
  id: string;
  hand: Hand;
  tickets: TicketResult[];
  ticketOffer: { tickets: Ticket[]; minKeep: number; initial: boolean } | null;
}
export interface GameView {
  status: GameState["status"];
  players: PublicPlayer[];
  currentPlayerId: string;
  self: SelfView | null;
  market: CardColor[];
  trainDeckCount: number;
  trainDiscardCount: number;
  ticketDeckCount: number;
  claimedRoutes: Record<string, string>;
  stations: Record<string, string>;
  drawnTrainCards: number;
  tunnel: TunnelAttempt | null;
  turn: number;
  finalTurnsRemaining: number | null;
  finalRoundPlayerId: string | null;
  log: GameLog[];
  results: PlayerResult[] | null;
  winnerIds: string[];
}
